import { useEffect, useState } from 'react';
import { completeBatch } from '../../lib/labData';
import { containerCount, expiryDate, yieldPercent } from '../../lib/lab';
import { todayISO } from '../../lib/dates';

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });

// What actually came out. Short on purpose — the one field that matters is
// pre-filled, so the common case is a single tap.
export default function CompleteBatchDialog({ batch, onClose, onSaved }) {
  const planned = containerCount(batch.containers);
  const [units, setUnits] = useState(planned || '');
  const [grams, setGrams] = useState('');
  const [dateCompleted, setDateCompleted] = useState(todayISO());
  const [notes, setNotes] = useState(batch.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pct = yieldPercent(units, planned);
  const months = batch.formula?.family?.product_type?.shelf_life_months;
  const expires = expiryDate(dateCompleted, months);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await completeBatch(batch, {
        dateCompleted,
        actualUnits: units,
        actualG: grams,
        notes,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="asset-modal-backdrop" onClick={onClose}>
      <div
        className="version-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Complete batch"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="asset-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <header className="version-modal-head">
          <div>
            <h3>Batch complete</h3>
            <span className="formula-detail-type">{batch.batch_code}</span>
          </div>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="lab-form-grid">
            <label>
              Actual yield (units)
              <input
                type="number"
                min="0"
                step="1"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
              />
            </label>
            <label>
              Actual yield (g)
              <input
                type="number"
                min="0"
                step="any"
                value={grams}
                onChange={(e) => setGrams(e.target.value)}
                placeholder="optional"
              />
            </label>
            <label>
              Date made
              <input
                type="date"
                value={dateCompleted}
                onChange={(e) => setDateCompleted(e.target.value)}
              />
            </label>
          </div>

          {/* Informational, never an error — a short yield is a fact about
              the batch, not a mistake in the form. */}
          {pct != null && planned > 0 && pct !== 100 && (
            <p className="batch-form-hint">
              {units} of {planned} planned — {pct}% yield
            </p>
          )}

          <label className="batch-notes-field">
            Notes
            <textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="How did it turn out? Texture, scent, anything to change next time."
            />
          </label>

          <p className="batch-form-hint">
            Batch code {batch.batch_code}
            {expires ? ` · Expires ${MONTH.format(new Date(`${expires}T12:00:00`))}` : ''}
            {!months && ' · No shelf life set for this product type, so no expiry is recorded'}
          </p>

          {error && <p className="formula-error">{error}</p>}

          <div className="ingredient-form-actions">
            <button type="button" className="ghost-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-action" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
